import React from "react";

import { toast } from "react-toastify";

import { selectTenant } from "containers/Login/reducer";

import * as AccountAPI from "api/AccountAPI";
import * as AdminAPI from "api/AdminAPI";
import * as TenantAPI from "api/TenantAPI";
import { getUserToken, getTenantToken } from "containers/Login/reducer";
import { isEnabled } from "@/utils/featureFlags";
import * as BaseAPI from "api/BaseAPI";

import * as AuthAPI from "api/AuthAPI";

import * as loginReducer from "containers/Login/reducer";

const UPDATE_FIELD = "admin/UPDATE_FIELD";
const INIT_ADMIN = "admin/INIT_ADMIN";
const UPDATE_USER_ADD = "admin/UPDATE_USER_ADD";

const initialState = {
  type: "null",
  selectedOrganization: "",
  organizationID: -1,
  users: [],
  accounts: [],
  selectedTenantSharing: false,
  deleteAWSAcc: false,
  deleteTarget: {},
  isLoadingAccounts: true,
  isLoadingUsers: true,
  userTenants: []
};

function adminReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    case INIT_ADMIN:
      return {
        ...state,
        ...action.data
      };
    default:
      return state;
  }
}

export function autoAcceptInvites(tenants, hideToasts = false) {
  return (dispatch, getState) => {
    const promises = tenants.map((tenant) => {
      const status = (tenant.invite || {}).status;

      switch (status) {
        case "REQUESTED":
          return dispatch(autoAcceptInvite(tenant, hideToasts));

        default:
          return Promise.resolve(tenant);
      }
    });

    return Promise.all(promises).then((processedTenants) =>
      Promise.resolve(
        processedTenants.filter((tenant) => tenant && tenant.invite && tenant.invite.status == "ACCEPTED")
      )
    );
  };
}

function autoAcceptInvite(tenant, hideToasts = false) {
  return (dispatch, getState) => {
    if (tenant.user_limit === "unlimited" || tenant.num_licenses < tenant.user_limit) {
      return dispatch(acceptTenantInvite(tenant, hideToasts));
    } else {
      return dispatch(handleTenantAtCapacity(tenant));
    }
  };
}

function acceptTenantInvite(tenant, hideToasts = false) {
  return (dispatch, getState) => {
    return AdminAPI.respondToInvite(tenant.id, "ACCEPTED", getUserToken(getState()))
      .then((response) => {
        if (!hideToasts) {
          toast("You are now a member of tenant " + tenant.fullname + ".", {
            type: toast.TYPE.SUCCESS,
            position: toast.POSITION.TOP_CENTER,
            autoClose: 7500
          });
        }

        return Promise.resolve(response.tenant);
      })
      .catch(() => dispatch(handleTenantAtCapacity(tenant)));
  };
}

function handleTenantAtCapacity(tenant) {
  return (dispatch, getState) => {
    return AdminAPI.rejectInvite(tenant.id, getUserToken(getState())).then(() => {
        toast("You were invited to tenant " + tenant.fullname + " but could not accept as the tenant is full.", {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });

        return Promise.resolve(tenant);
      }).catch((e) => {
        console.log(e);
        return Promise.resolve(tenant);
      });
  };
}

export function fetchAccounts() {
  return (dispatch, getState) => {
    dispatch(updateIsLoadingAccounts(true));
    const workspaceID = getSelectedOrganization(getState());
    if (!workspaceID || workspaceID < 1) {
      dispatch(updateIsLoadingAccounts(false));
      return [];
    }

    const token = getTenantToken(getState(), workspaceID);

    AccountAPI.getAccounts(token).then((data) => {
      dispatch(updateAccounts(data));
      dispatch(updateIsLoadingAccounts(false));
    });
  };
}

export function updateAccountConfig(id, config) {
  return (dispatch, getState) => {
    return AccountAPI.updateAccount(id, config).then((data) => {
      if (data) {
        // not sure if better check for failed API call return
        toast("Azure settings for " + data.name + " have been applied!", {
          type: toast.TYPE.SUCCESS,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000
        });
      }
    });
  };
}

export function sendInviteFromTenant(tenantID, email) {
  return (dispatch, getState) => {
    var userAuth = getUserToken(getState());

    return AdminAPI.sendInvite(tenantID, email, userAuth).then((data) => {
      toast(email + " has been invited!", {
        type: toast.TYPE.SUCCESS,
        position: toast.POSITION.TOP_CENTER,
        autoClose: 7500
      });

      //refresh incase of existing user added
      dispatch(updateUsersCall(loginReducer.getSelectedTenant(getState())));
    });
  };
}

// This method is a temporary method for bulk adding users. Send emails in chunks of 10 with error toasts suppressed.
// Ideally needs to be updated with a backend call for bulk adding rather than going through requests individually.
// Email validation is done on the front end through basic regex expression mainly checking valid characters, a @, and a . followed by 2 characters.
export function inviteMultipleFromTenant(tenantID, emailArray) {
  return async (dispatch, getState) => {
    const userAuth = getUserToken(getState());
    const errorEmails = [];
    const chunkSize = 10;

    for (let i = 0; i < emailArray.length; i += chunkSize) {
      const chunk = emailArray.slice(i, i + chunkSize);
      // console.log(chunk);
      // console.log(`Sending chunk from ${i} to ${Math.min(i + chunkSize - 1, emailArray.length)}`);
      await Promise.all(
        chunk.map(async (email) => {
          return AdminAPI.sendInvite(tenantID, email, userAuth, true).catch((error) => {
            return errorEmails.push(email);
          });
        })
      );
    }
    dispatch(updateUsersCall(loginReducer.getSelectedTenant(getState())));
    return errorEmails;
  };
}

export function removeUserFromTenant(tenantID, email) {
  return (dispatch, getState) => {
    const userAuth = getUserToken(getState());

    return AdminAPI.removeFromTenant(tenantID, email, userAuth)
      .then((data) => {
        toast(email + " has been removed.", {
          type: toast.TYPE.SUCCESS,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });

        dispatch(updateUsersCall(loginReducer.getSelectedTenant(getState())));
      })
      .catch((e) => {
        console.log(e);
        toast(email + " could not be removed.", {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });
      });
  };
}

export function updateUsersCall(tenant) {
  return (dispatch, getState) => {
    dispatch(updateIsLoadingUsers(true));
    if (!tenant || !tenant.id || tenant.id === -1) {
      dispatch(updateIsLoadingUsers(false));
      return [];
    }
    var userAuth = tenant && tenant.invite && tenant.invite.token;

    TenantAPI.getUsersOfTenant(userAuth, tenant.id).then((data) => {
      dispatch(updateUsers(data));
      dispatch(updateIsLoadingUsers(false));
    });
  };
}

export function resetUserPassword(email) {
  return (dispatch, getState) => {
    dispatch(updateIsLoadingUsers(true));

    return AuthAPI.postUser(email)
      .then((response) => {
        return AuthAPI.resetPassword(response.token)
          .then((tResponse) => {
            if (
              (tResponse && tResponse.tags && tResponse.tags === "PASSWORD_RESET_REQUIRED") ||
              tResponse.tags?.includes("PASSWORD_RESET_REQUIRED")
            ) {
              toast("Please have the user check their email to finish resetting their password.", {
                type: toast.TYPE.SUCCESS,
                position: toast.POSITION.TOP_CENTER,
                autoClose: 7500
              });
            }
            return dispatch(updateUsersCall(loginReducer.getSelectedTenant(getState())));
          })
          .catch((error) => {
            dispatch(updateIsLoadingUsers(false));
            return toast("There was an issue with this password reset.", {
              type: toast.TYPE.ERROR,
              position: toast.POSITION.TOP_CENTER
            });
          });
      })
      .catch((error) => {
        dispatch(updateIsLoadingUsers(false));
        return toast("There was an issue with this password reset.", {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER
        });
      });
  };
}

export function updateUserPassword(newPassword, user, currentPassword = undefined) {
  return async (dispatch, getState) => {
    const userAuth = getUserToken(getState());

    const currentUserEmail = loginReducer.getUserEmail(getState());
    const isCurrentUser = currentUserEmail === user?.email;

    const password = {
      new: newPassword
    };

    if (!isCurrentUser && isEnabled("admin-force-update-password")) {
      return await AuthAPI.updateUserPassword({ password }, userAuth, user?.email);
    }

    if (currentPassword) {
      password.current = currentPassword;
    }

    return await AuthAPI.updateUserPassword({ password }, userAuth);
  };
}

/*--Selectors--*/

export function getIsLoadingUsers(state) {
  return getAdmin(state).isLoadingUsers;
}

export function getIsLoadingAccounts(state) {
  return getAdmin(state).isLoadingAccounts;
}

export function getSelectedTenantSharing(state) {
  return getAdmin(state).selectedTenantSharing;
}

export function getIsSharedWorkspace(state) {
  return false; //add the state!!
}

export function getAccounts(state) {
  return getAdmin(state).accounts;
}

export function getDeleteTarget(state) {
  return getAdmin(state).deleteTarget;
}

export function getDeleteAWSAcc(state) {
  return getAdmin(state).deleteAWSAcc;
}

export function getUsers(state) {
  return getAdmin(state).users;
}

export function getActiveUsers(state) {
  return getUsers(state)?.filter((u) => u.type === "USER");
}

export function getSelectedOrganization(state) {
  return getAdmin(state).selectedOrganization;
}

export function getOrganizationID(state) {
  return getAdmin(state).organizationID;
}

export function getType(state) {
  return getAdmin(state).type;
}

export function getAdmin(state) {
  return state.admin;
}

/*--Action Creators--*/

export function initAdmin(data) {
  return { type: INIT_ADMIN, data: data };
}

export function updateIsLoadingUsers(value) {
  return { type: UPDATE_FIELD, data: value, key: "isLoadingUsers" };
}

export function updateIsLoadingAccounts(value) {
  return { type: UPDATE_FIELD, data: value, key: "isLoadingAccounts" };
}

export function updateSelectedTenantSharing(value) {
  return { type: UPDATE_FIELD, data: value, key: "selectedTenantSharing" };
}

export function updateAccounts(value) {
  return { type: UPDATE_FIELD, data: value, key: "accounts" };
}

export function updateDeleteTarget(value) {
  return { type: UPDATE_FIELD, data: value, key: "deleteTarget" };
}

export function updateUsers(users) {
  return { type: UPDATE_FIELD, data: users, key: "users" };
}

export function updateDeleteAWSAcc(value) {
  return { type: UPDATE_FIELD, data: value, key: "deleteAWSAcc" };
}

export function updateSelectedOrganization(organization) {
  return {
    type: UPDATE_FIELD,
    data: organization,
    key: "selectedOrganization"
  };
}

export function updateOrganizationID(id) {
  return { type: UPDATE_FIELD, data: id, key: "organizationID" };
}

export function updateType(type) {
  return { type: UPDATE_FIELD, data: type, key: "type" };
}

export default adminReducer;